import { Link } from "react-router-dom";
import Logo from "../components/Logo";
import ThemeToggle from "../components/ThemeToggle";
import Footer from "../components/Footer";
import { useEffect } from "react";
import PublicNav from "../components/PublicNav";

export default function LabourMarketIndex() {
  useEffect(() => {
    document.title = "Joborigo: Labour Market Index";
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-brand-dark-bg text-gray-900 dark:text-gray-100">
      <PublicNav />

      <main className="flex-1 max-w-4xl mx-auto px-6 py-16 w-full">
        {/* Preview banner */}
        <div className="mb-12 px-4 py-3 rounded-lg border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-900/10 text-amber-900 dark:text-amber-200 text-sm">
          <strong>Preview.</strong> The Labour Market Index publishes its first
          edition once Joborigo has accumulated sufficient anonymised
          application data. Numbers below are placeholders illustrating the
          report's structure and intended methodology.
        </div>

        {/* Header */}
        <div className="mb-16">
          <p className="text-sm font-medium text-brand-violet dark:text-brand-violet-on-dark mb-3 uppercase tracking-widest">
            Quarterly · Open Data
          </p>
          <h1 className="text-5xl font-extrabold mb-4 leading-tight">
            Joborigo Labour Market Index
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 leading-relaxed">
            An honest quarterly report on what is actually happening in the
            Swedish and Nordic job market, built from real application data,
            not surveys or self-reports.
          </p>
        </div>

        {/* Hero finding */}
        <section className="mb-20">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-widest mb-4">
            Q1 2027 · Headline finding
          </p>
          <p className="text-3xl md:text-4xl font-bold leading-tight mb-6">
            <span className="text-brand-violet dark:text-brand-violet-on-dark">42%</span> of applications in
            Tech received no response within 30 days. The median
            time-to-first-response was{" "}
            <span className="text-brand-violet dark:text-brand-violet-on-dark">11 days</span>: three days
            longer than the same period last year.
          </p>
          <p className="text-gray-500 dark:text-gray-400 leading-relaxed">
            Ghost rates remain the strongest signal of employer engagement
            health. Companies that respond within 7 days are 4.3× more likely to
            extend an offer than those that respond after 14 days.
          </p>
        </section>

        {/* Supporting findings */}
        <section className="mb-20">
          <h2 className="text-sm font-medium text-gray-400 uppercase tracking-widest mb-8">
            Supporting findings
          </h2>

          <div className="space-y-10">
            <div className="border-l-2 border-brand-violet dark:border-brand-violet-on-dark pl-6">
              <p className="text-2xl font-bold mb-2">
                Cover letters correlated with a{" "}
                <span className="text-brand-violet dark:text-brand-violet-on-dark">23%</span> higher response
                rate in Tech, but had{" "}
                <span className="text-brand-violet dark:text-brand-violet-on-dark">no measurable impact</span>{" "}
                in trades and services.
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Based on 12,400 applications across 6 industry categories.
              </p>
            </div>

            <div className="border-l-2 border-brand-violet dark:border-brand-violet-on-dark pl-6">
              <p className="text-2xl font-bold mb-2">
                Applications submitted in the first{" "}
                <span className="text-brand-violet dark:text-brand-violet-on-dark">2 hours</span> after a job
                ad went live had a{" "}
                <span className="text-brand-violet dark:text-brand-violet-on-dark">2.7×</span> higher hire rate
                than applications submitted in the same week.
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                The 2-hour window is the most decisive factor in application
                outcomes, more than CV polish, cover letter length, or referral
                status.
              </p>
            </div>

            <div className="border-l-2 border-brand-violet dark:border-brand-violet-on-dark pl-6">
              <p className="text-2xl font-bold mb-2">
                The median stated salary range understated final offers by{" "}
                <span className="text-brand-violet dark:text-brand-violet-on-dark">8.4%</span> for senior roles
                in Tech and Finance.
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Negotiation matters most where the public range is narrow.
              </p>
            </div>

            <div className="border-l-2 border-brand-violet dark:border-brand-violet-on-dark pl-6">
              <p className="text-2xl font-bold mb-2">
                Companies with verified candidate timelines hired{" "}
                <span className="text-brand-violet dark:text-brand-violet-on-dark">31%</span> faster than those
                relying on self-reported CVs alone.
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Verification reduces decision friction.
              </p>
            </div>
          </div>
        </section>

        {/* Methodology */}
        <section className="mb-20">
          <h2 className="text-sm font-medium text-gray-400 uppercase tracking-widest mb-6">
            Methodology
          </h2>
          <div className="space-y-4 text-gray-600 dark:text-gray-300 leading-relaxed">
            <p>
              The Joborigo Labour Market Index is built from anonymised,
              aggregated application data submitted by Joborigo users with their
              explicit consent. No individual application, identity, or employer
              is ever published.
            </p>
            <p>
              We publish only findings supported by sufficient sample size and
              clear statistical significance. Where data is thin, we say so.
              Where findings are noisy, we omit them rather than overstate.
            </p>
            <p>
              The Index is published once per quarter. Each edition includes its
              underlying methodology, sample sizes, and any caveats relevant to
              the period. A downloadable PDF is provided for each quarterly
              release.
            </p>
          </div>
        </section>

        {/* What's not in the report */}
        <section className="mb-20">
          <h2 className="text-sm font-medium text-gray-400 uppercase tracking-widest mb-6">
            What this report is not
          </h2>
          <ul className="space-y-3 text-gray-600 dark:text-gray-300 leading-relaxed">
            <li className="flex gap-3">
              <span className="text-brand-violet dark:text-brand-violet-on-dark mt-1">•</span>
              <span>
                Not a survey. Surveys measure stated intent, not actual
                behaviour. The Index reports what people did.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="text-brand-violet dark:text-brand-violet-on-dark mt-1">•</span>
              <span>
                Not a complete picture of the labour market. The Index reports
                what is observable through Joborigo. Other channels exist.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="text-brand-violet dark:text-brand-violet-on-dark mt-1">•</span>
              <span>
                Not a ranking of employers. The Index does not name companies.
                Aggregate signal is published; individual reputations are not.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="text-brand-violet dark:text-brand-violet-on-dark mt-1">•</span>
              <span>
                Not for sale. The Index is published openly and freely. If it is
                useful to you, share it.
              </span>
            </li>
          </ul>
        </section>

        {/* Acknowledgments */}
        <section className="mb-20">
          <h2 className="text-sm font-medium text-gray-400 uppercase tracking-widest mb-6">
            Acknowledgments
          </h2>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            The Index is made possible by every Joborigo user who chose to
            contribute their anonymised application data. Specific
            acknowledgments to early Founding Partner agencies and collaborators
            will appear with each quarterly edition.
          </p>
        </section>

        {/* Cadence / next edition */}
        <div className="border-t border-gray-100 dark:border-white/10 pt-8">
          <p className="text-sm text-gray-400 mb-2">Next edition</p>
          <p className="text-base text-gray-600 dark:text-gray-300">
            The first edition publishes once a sufficient sample is available.
            We will not publish for the sake of publishing.
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
}
